import { Flex, Icon, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import negativeSentimentIcon from '~/assets/svgs/icon-sentiment-negative.svg?react'
import neutralSentimentIcon from '~/assets/svgs/icon-sentiment-neutral.svg?react'
import positiveSentimentIcon from '~/assets/svgs/icon-sentiment-positive.svg?react'
import Ellipse from '~/components/Ellipse'
import { colorPalette } from '~/themes/colors/colorPalette'
import { textStyleKeys } from '~/themes/textStyles'
import { CallTranscript } from '~/types/CallTranscript'
import { isInRange } from '~/utils/math'

import { SectionHeader } from './SectionHeader'

interface SentimentByQuarterSectionProps {
  transcript: CallTranscript
}

enum SentimentDataKeys {
  AGENT = 'agentScore',
  CUSTOMER = 'customerScore',
}

interface ChartDotProps<PayloadType> {
  payload?: PayloadType
  dataKey?: string
  cx: number
  cy: number
  index?: number
}

const ChartDot = ({
  payload,
  dataKey,
  cx,
  cy,
}: ChartDotProps<{
  payload: { agentScore: number; customerScore: number }
  dataKey: string
  cx: number
  cy: number
}>) => {
  const score = payload![dataKey as SentimentDataKeys]
  const dotColor = isInRange(score, 0, 2)
    ? colorPalette.gray[900]
    : score < 0
      ? colorPalette.red[700]
      : colorPalette.green[600]

  return (
    <svg
      x={cx - 6}
      y={cy - 6}
      width={12}
      height={12}
      stroke={colorPalette.gray[100]}
      strokeWidth={1}
    >
      <g transform="translate(6 6)">
        <circle r="6" fill={dotColor} />
      </g>
    </svg>
  )
}

const getChartDot = ({
  payload,
  dataKey,
  cx,
  cy,
  index,
}: ChartDotProps<{
  payload: { agentScore: number; customerScore: number }
  dataKey: string
  cx: number
  cy: number
  index: number
}>) => {
  return <ChartDot key={index} {...{ payload, dataKey, cx, cy }} />
}

const defaultSentimentValue: { Score: number }[] = [
  { Score: 0 },
  { Score: 0 },
  { Score: 0 },
  { Score: 0 },
]

export const SentimentByQuarterSection = ({
  transcript,
}: SentimentByQuarterSectionProps) => {
  const agentSentiment =
    transcript.ConversationCharacteristics.Sentiment.SentimentByPeriod.QUARTER
      .AGENT ?? defaultSentimentValue
  const customerSentiment =
    transcript.ConversationCharacteristics.Sentiment.SentimentByPeriod.QUARTER
      .CUSTOMER ?? defaultSentimentValue

  //graph data needs to be memoized or the dots will not render all the time:
  //https://github.com/recharts/recharts/issues/1821 (second to last comment)
  const graphData: { agentScore: number; customerScore: number }[] =
    useMemo(() => {
      const data: { agentScore: number; customerScore: number }[] = []
      for (let i = 0; i < agentSentiment.length; i++) {
        data.push({
          agentScore: agentSentiment[i].Score,
          customerScore: customerSentiment[i].Score,
        })
      }
      return data
    }, [transcript])

  const customerColor = colorPalette.gray[1300]
  const agentColor = colorPalette.gray[700]

  return (
    <Flex flexDirection="column" width="100%" gap="0">
      <SectionHeader
        title="Sentiment by Quarter"
        tooltipText="This shows sentiment for the patient and office, reflecting the emotional tone of the conversation. Each quarter is classified as positive (green), neutral (gray), or negative (red)."
      />
      <Flex flexDirection="row" gap="1.25rem" height="150px">
        <Flex
          width="1.5rem"
          flexDirection="column"
          justifyContent="space-between"
          paddingBottom="0.75rem"
          paddingTop="0.5rem"
        >
          <Icon
            height="1.65rem"
            width="1.65rem"
            borderRadius="1.5rem"
            as={positiveSentimentIcon}
          />
          <Icon
            height="1.65rem"
            width="1.65rem"
            borderRadius="1.5rem"
            as={neutralSentimentIcon}
          />
          <Icon
            height="1.65rem"
            width="1.65rem"
            borderRadius="1.5rem"
            as={negativeSentimentIcon}
          />
        </Flex>
        <Flex margin="0px -12px 0px 0px" flex="1 0 0%" height="150px">
          <ResponsiveContainer width="100%" height={150}>
            <LineChart
              height={150}
              margin={{ left: 12, top: 12, right: 12, bottom: 12 }}
              data={graphData}
            >
              <CartesianGrid
                stroke={colorPalette.gray[400]}
                vertical={false}
                horizontalPoints={[12, 75, 138]}
              />
              <XAxis hide padding={{ left: 6, right: 6 }} />
              <YAxis hide domain={[-5, 5]} />
              <Line
                type="linear"
                dataKey="customerScore"
                stroke={customerColor}
                strokeWidth={4}
                dot={(props) => getChartDot(props)}
                isAnimationActive={false}
              />
              <Line
                type="linear"
                dataKey="agentScore"
                stroke={agentColor}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={(props) => getChartDot(props)}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Flex>
      </Flex>
      <Flex
        flexDirection="row"
        justifyContent="end"
        justifyItems="center"
        alignContent="center"
        gap="1.25rem"
      >
        <Flex alignItems="center">
          <Ellipse
            height={8}
            width={28}
            color={customerColor}
            margin="0px 5px 0px 0px"
          />
          <Text textStyle={textStyleKeys.Body2}>Patient</Text>
        </Flex>
        <Flex alignItems="center" gap="5px">
          <svg width="28" height="4">
            <line
              x1="1"
              y1="2"
              x2="26"
              y2="2"
              stroke={agentColor}
              strokeWidth="4"
              strokeDasharray="4 3"
              strokeLinecap="butt"
            />
            <circle cx="2" cy="2" r="2" fill={agentColor} />
            <circle cx="25" cy="2" r="2" fill={agentColor} />
          </svg>
          <Text textStyle={textStyleKeys.Body2}>Office</Text>
        </Flex>
      </Flex>
    </Flex>
  )
}
